import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const avatarVariants = cva(
  cn(
    'relative inline-flex shrink-0 items-center justify-center',
    'overflow-hidden bg-gray-100 text-gray-600',
    'border border-gray-200'
  ),
  {
    variants: {
      size: {
        sm: 'h-8 w-8 text-xs',
        md: 'h-9 w-9 text-sm',
        lg: 'h-10 w-10 text-base',
      },
      shape: {
        round: 'rounded-full',
        roundrect: 'rounded-md',
      },
    },
    defaultVariants: {
      size: 'md',
      shape: 'round',
    },
  }
)

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  fallback?: string
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size, shape, src, alt, fallback, children, ...props }, ref) => {
    const [imageError, setImageError] = React.useState(false)

    return (
      <div
        className={cn(avatarVariants({ size, shape }), className)}
        ref={ref}
        {...props}
      >
        {src && !imageError ? (
          <img
            alt={alt || 'Avatar'}
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
            src={src}
          />
        ) : (
          <span className="font-medium">
            {children || fallback || alt?.charAt(0)?.toUpperCase() || '?'}
          </span>
        )}
      </div>
    )
  }
)
Avatar.displayName = 'Avatar'

export { Avatar, avatarVariants }
